import { yupResolver } from "@hookform/resolvers/yup";

import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Modal } from "@/components/General/Modal/modal";
import { APIStatus } from "@/redux/modules/api/data/wishlist/wishlist.module";
import { ModalType } from "@/redux/modules/value/modal/modal.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { TApiAction } from "@/redux/store.types";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { OmniFormContextProvider } from "@/utils/forms/omniForm.provider";

import {
    WishlistCreateListFormValidationSchema,
    WishlistCreateListFormValues
} from "./WishlistCreateListModal.schema";

const FORM_ID = "wishlist-create";

type WishlistCreateListModalComponentProps = {
    open: boolean;
    onCreate: (name: string) => Promise<TApiAction>;
    onCancel: () => void;
    isLoading: boolean;
};

const WishlistCreateListModalComponent = ({
    open,
    onCreate,
    onCancel,
    isLoading
}: WishlistCreateListModalComponentProps) => {
    const { t } = useGranite();

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            name: ""
        },
        resolver: yupResolver(WishlistCreateListFormValidationSchema)
    });

    const onSubmit = ({ name }: WishlistCreateListFormValues) => {
        onCreate(name).then((action) => {
            if (action.type === APIStatus.SUCCESS) {
                onCancel();
            }
        });
    };

    return (
        <Modal
            modalType={ModalType.WishlistCreate}
            title={t("wishlist.create.modal.title")}
            open={open}
            autoFocus={true}
            formId={FORM_ID}
            centered={true}
            withFooter={true}
            isLoading={isLoading}
            setOpen={onCancel}
            confirmButtonText={t("wishlist.create.form.submit")}
        >
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={false}
            >
                {() => (
                    <OmniFormContextProvider {...methods}>
                        <form
                            id={FORM_ID}
                            onSubmit={methods.handleSubmit(onSubmit)}
                        >
                            <TextField<WishlistCreateListFormValues>
                                label={t("wishlist.create.form.name.label")}
                                name="name"
                                required={true}
                                placeholder={t("wishlist.name.placeholder")}
                            />
                        </form>
                    </OmniFormContextProvider>
                )}
            </FormInfoProvider>
        </Modal>
    );
};

export { WishlistCreateListModalComponent };
